import {createSelector} from '@reduxjs/toolkit'

import {removeHyphen} from '@shared/helpers/format'
import {replaceMasking, shortenText} from '@shared/helpers/string'

import {checkPersonalCustomsClearanceAddress} from '@web/helpers/policies'
import {personalCustomsClearanceSelector} from '@web/slices/personalCustomsClearance/slice'

import type {DeliveryInfoState} from '@web/slices/deliveryInfo/slice'

const getShortenReceiverName = shortenText(15)

type RootState = {deliveryInfo: DeliveryInfoState}

const addressesInfoSelector = (state: RootState) => state.deliveryInfo.selected.addressInfo

export const personalCustomsClearanceSavedSelector = createSelector(
    personalCustomsClearanceSelector,
    addressesInfoSelector,
    ({saved, selected}, selectedAddress) => {
        const {hash, receiverName, telNo1, zipCode} = selectedAddress ?? {
            hash: '',
            receiverName: '',
            telNo1: '',
            zipCode: '',
        }
        if (
            selected.id === hash &&
            checkPersonalCustomsClearanceAddress(selected, {
                receiverName,
                telNo1,
            })
        ) {
            // 선택했던 배송이지 일치한다면
            return {
                ...selected,
                editableUser: true,
            }
        }
        if (
            checkPersonalCustomsClearanceAddress(saved, {
                receiverName,
                telNo1,
            }) ||
            // @see https://oss.fin.navercorp.com/Product-Planning/Order_QA/issues/8747
            // 개인화정보 이름/연락처 null인 경우 통관부호가 어떤 이름/연락처와 일치하는지 알 수 없기 때문에, 배송지 정보와 무관하게 항상 개인화정보 통관부호의 입력 유지하고자 함
            (!(saved.name && saved.phone) && saved.view)
        ) {
            // 변경되는 연락처가 기존 정보와 일치한다면
            return {
                ...saved,
                zipCode,
                editableUser: true,
            }
        }
        return {
            view: '',
            hash: '',
            name: '',
            phone: '',
            zipCode,
            editableUser: false,
        }
    },
)

/** 해외통관 고유부호 정보 */
export const personalCustomsClearanceUserInfoSelector = createSelector(
    personalCustomsClearanceSelector,
    personalCustomsClearanceSavedSelector,
    addressesInfoSelector,
    ({saved}, selected, selectedAddress) => {
        const {receiverName, telNo1, zipCode} = selectedAddress ?? {
            receiverName: '',
            telNo1: '',
            zipCode: '',
        }

        return {
            personalCustomsCode: selected.view,
            personalCustomsCodeHash: selected.hash,
            hash: saved.hash,
            selected: {
                view: selected.view,
                hash: selected.hash,
                name: receiverName,
                phone: telNo1,
            },
            editableUser: selected.editableUser,
            checkMFAUser: selected.editableUser,
            existingCode: replaceMasking(selected.view, selected.view.length - 5),
            receiverName: getShortenReceiverName(receiverName),
            telNo1: removeHyphen(telNo1),
            zipCode,
        }
    },
)
